import { logger } from "@/utils/logger";
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDealerSession } from '@/contexts/DealerSessionContext';
interface PaymentOption {
  term: number;
  rate: string;
  payment: {
    weekly?: string;
    biweekly?: string;
    monthly?: string;
  };
  residualPerc?: number;
  kmAllowance?: number;
}

interface PaymentAPIResponse {
  finance?: {
    [key: string]: PaymentOption;
  };
  lease?: {
    [key: string]: {
      [key: string]: PaymentOption;
    };
  };
}

export interface AvailableOptions {
  terms: number[];
  kmAllowances: number[];
}

export interface SelectedPayment {
  amount: string;
  rate: string;
  term: number;
  kmAllowance?: number;
  type: 'Finance' | 'Lease';
  frequency: string;
}

export const useInteractivePaymentSelector = (
  identifier: string | number | undefined,
  initialPaymentType?: 'Finance' | 'Lease',
  initialTerm?: number,
  initialKmAllowance?: number,
  frequency: string = 'monthly',
  paramType: 'styleid' | 'vin' = 'styleid'
) => {
  const { language } = useLanguage();
  const { sessionData } = useDealerSession();
  const [apiData, setApiData] = useState<PaymentAPIResponse | null>(null);
  const [paymentType, setPaymentType] = useState<'Finance' | 'Lease'>(initialPaymentType || 'Lease');
  const [availableOptions, setAvailableOptions] = useState<AvailableOptions>({
    terms: [],
    kmAllowances: []
  });
  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);
  const [selectedKmAllowance, setSelectedKmAllowance] = useState<number | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<SelectedPayment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // CRITICAL DEBUG: Log sessionData state for payment banner debugging
  logger.log('💳🔍 useInteractivePaymentSelector INIT:', {
    identifier,
    paramType,
    sessionDataExists: !!sessionData,
    domain: sessionData?.domain,
    paymentsEnabled: sessionData?.paymentsEnabled
  });

  // Sync payment type with external changes (from other cards)
  useEffect(() => {
    if (initialPaymentType && initialPaymentType !== paymentType) {
      setPaymentType(initialPaymentType);
    }
  }, [initialPaymentType]);

  // Fetch payment data
  useEffect(() => {
    // Wait for sessionData.domain before fetching
    if (!sessionData?.domain) {
      logger.log('💳 useInteractivePaymentSelector: Waiting for sessionData.domain...', {
        identifier,
        paramType,
        sessionDataExists: !!sessionData
      });
      return;
    }
    
    if (!identifier) return;

    // CRITICAL: Compute dealerBaseUrl INSIDE the useEffect to ensure we use the current domain
    const dealerBaseUrl = `https://${sessionData.domain.replace(/^https?:\/\//, '')}`;
    
    logger.log('💳 useInteractivePaymentSelector: Starting fetch', {
      identifier,
      paramType,
      dealerBaseUrl
    });

    const fetchPaymentData = async () => {
      setLoading(true);
      setError(null);
      try {
        const paramName = paramType === 'vin' ? 'VIN' : 'styleid';
        const url = `${dealerBaseUrl}/ajax/getPaymentForVehicleVIN?${paramName}=${identifier}`;
        
        logger.log(`🎯 useInteractivePaymentSelector: Fetching for ${paramType}:`, identifier, 'URL:', url);

        const response = await fetch(url, {
          headers: { Accept: 'application/json' }
        });
        
        if (paramType === 'vin') {
        }
        
        if (!response.ok) throw new Error('Failed to fetch payment data');
        
        const data: PaymentAPIResponse = await response.json();
        
        if (paramType === 'vin') {
          logger.log(`✅ useInteractivePaymentSelector: Data received for ${paramType} ${identifier}:`, {
            hasFinance: !!data.finance,
            hasLease: !!data.lease,
            financeKeys: data.finance ? Object.keys(data.finance) : [],
            leaseKeys: data.lease ? Object.keys(data.lease) : []
          });
        }
        setApiData(data);

        // Extract available options
        const terms: number[] = [];
        const kmAllowances: number[] = [];

        if (paymentType === 'Finance' && data.finance) {
          Object.keys(data.finance).forEach(key => {
            const match = key.match(/(\d+)-mth/);
            if (match) {
              const term = parseInt(match[1]);
              if (!terms.includes(term)) terms.push(term);
            }
          });
        } else if (paymentType === 'Lease' && data.lease) {
          Object.keys(data.lease).forEach(kmKey => {
            const kmMatch = kmKey.match(/(\d+)km/);
            if (kmMatch) {
              const km = parseInt(kmMatch[1]);
              if (!kmAllowances.includes(km)) kmAllowances.push(km);
            }

            // Get terms from first km bucket
            if (kmAllowances.length === 1) {
              Object.keys(data.lease![kmKey]).forEach(termKey => {
                const termMatch = termKey.match(/(\d+)-mth/);
                if (termMatch) {
                  const term = parseInt(termMatch[1]);
                  if (!terms.includes(term)) terms.push(term);
                }
              });
            }
          });
        }

        terms.sort((a, b) => a - b);
        kmAllowances.sort((a, b) => a - b);

        if (paramType === 'vin') {
          logger.log(`📊 useInteractivePaymentSelector: Available options for VIN ${identifier}:`, {
            terms,
            kmAllowances,
            paymentType
          });
        }

        setAvailableOptions({ terms, kmAllowances });

        // Set defaults based on initial preferences or fallback to defaults
        if (paymentType === 'Lease') {
          const defaultTerm = initialTerm && terms.includes(initialTerm) ? initialTerm : (terms.includes(36) ? 36 : terms[0]);
          const defaultKm = initialKmAllowance && kmAllowances.includes(initialKmAllowance) ? initialKmAllowance : (kmAllowances.includes(20000) ? 20000 : kmAllowances[0]);
          
          if (paramType === 'vin') {
            logger.log(`🎯 useInteractivePaymentSelector: Setting Lease defaults for VIN ${identifier}:`, {
              defaultTerm,
              defaultKm,
              initialTerm,
              initialKmAllowance,
              availableTerms: terms,
              availableKmAllowances: kmAllowances
            });
          }
          
          setSelectedTerm(defaultTerm);
          setSelectedKmAllowance(defaultKm);
        } else {
          const defaultTerm = initialTerm && terms.includes(initialTerm) ? initialTerm : (terms.includes(48) ? 48 : terms[0]);
          
          if (paramType === 'vin') {
            logger.log(`🎯 useInteractivePaymentSelector: Setting Finance defaults for VIN ${identifier}:`, {
              defaultTerm,
              initialTerm,
              availableTerms: terms
            });
          }
          
          setSelectedTerm(defaultTerm);
        }
      } catch (error) {
        console.error('Error fetching payment data:', error);
        setError(error instanceof Error ? error.message : 'Failed to load payment data');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, [identifier, paymentType, paramType, sessionData?.domain]); // Remove initialTerm/initialKmAllowance - they're only for initial state

  // Calculate selected payment
  useEffect(() => {
    if (paramType === 'vin') {
      logger.log('🧮 Payment calculation effect triggered (VIN):', { 
        hasApiData: !!apiData, 
        selectedTerm, 
        selectedKmAllowance, 
        paymentType, 
        frequency 
      });
    }
    
    if (!apiData || !selectedTerm) {
      if (paramType === 'vin') {
        logger.log('⚠️ Payment calculation skipped - missing apiData or selectedTerm (VIN path)');
      }
      return;
    }

    try {
      let paymentOption: PaymentOption | undefined;

      if (paymentType === 'Finance') {
        const termKey = `${selectedTerm}-mth`;
        paymentOption = apiData.finance?.[termKey];
        if (paramType === 'vin') {
          logger.log('💰 Finance payment lookup (VIN):', { termKey, found: !!paymentOption });
        }
      } else if (paymentType === 'Lease' && selectedKmAllowance) {
        const kmKey = `${selectedKmAllowance}km`;
        const termKey = `${selectedTerm}-mth`;
        paymentOption = apiData.lease?.[kmKey]?.[termKey];
        if (paramType === 'vin') {
          logger.log('💰 Lease payment lookup (VIN):', { kmKey, termKey, found: !!paymentOption, availableKmKeys: apiData.lease ? Object.keys(apiData.lease) : [] });
        }
      }

      if (paymentOption && paymentOption.payment[frequency as keyof typeof paymentOption.payment]) {
        const amount = paymentOption.payment[frequency as keyof typeof paymentOption.payment]!;
        if (paramType === 'vin') {
          logger.log('✅ Payment calculated successfully (VIN):', { amount, term: selectedTerm, type: paymentType });
        }
        setSelectedPayment({
          amount,
          rate: paymentOption.rate,
          term: selectedTerm,
          kmAllowance: selectedKmAllowance || undefined,
          type: paymentType,
          frequency
        });
      } else {
        if (paramType === 'vin') {
          logger.log('❌ Payment calculation failed (VIN):', { 
            hasPaymentOption: !!paymentOption, 
            hasFrequency: paymentOption ? !!paymentOption.payment[frequency as keyof typeof paymentOption.payment] : false,
            availableFrequencies: paymentOption ? Object.keys(paymentOption.payment) : []
          });
        }
      }
    } catch (error) {
      if (paramType === 'vin') {
        console.error('Error calculating VIN payment:', error);
      }
    }
  }, [apiData, selectedTerm, selectedKmAllowance, paymentType, frequency]);

  const cycleTerm = (direction: 'prev' | 'next') => {
    if (!selectedTerm || availableOptions.terms.length === 0) return;
    
    const currentIndex = availableOptions.terms.indexOf(selectedTerm);
    if (currentIndex === -1) return;

    let newIndex: number;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % availableOptions.terms.length;
    } else {
      newIndex = currentIndex === 0 ? availableOptions.terms.length - 1 : currentIndex - 1;
    }

    setSelectedTerm(availableOptions.terms[newIndex]);
  };

  const cycleKmAllowance = (direction: 'prev' | 'next') => {
    if (!selectedKmAllowance || availableOptions.kmAllowances.length === 0) return;
    
    const currentIndex = availableOptions.kmAllowances.indexOf(selectedKmAllowance);
    if (currentIndex === -1) return;

    let newIndex: number;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % availableOptions.kmAllowances.length;
    } else {
      newIndex = currentIndex === 0 ? availableOptions.kmAllowances.length - 1 : currentIndex - 1;
    }

    setSelectedKmAllowance(availableOptions.kmAllowances[newIndex]);
  };

  const changePaymentType = (newType: 'Finance' | 'Lease') => {
    setPaymentType(newType);
  };

  // Get rates for both payment types to display on radio buttons
  const getFinanceRate = () => {
    if (!apiData?.finance || !selectedTerm) return null;
    const termKey = `${selectedTerm}-mth`;
    return apiData.finance[termKey]?.rate || null;
  };

  const getLeaseRate = () => {
    if (!apiData?.lease || !selectedTerm || !selectedKmAllowance) return null;
    const kmKey = `${selectedKmAllowance}km`;
    const termKey = `${selectedTerm}-mth`;
    return apiData.lease[kmKey]?.[termKey]?.rate || null;
  };

  if (paramType === 'vin') {
    logger.log('🔄 useInteractivePaymentSelector RETURNING (VIN path):', {
      identifier,
      paramType,
      hasSelectedPayment: !!selectedPayment,
      selectedPayment: selectedPayment ? {
        amount: selectedPayment.amount,
        type: selectedPayment.type,
        term: selectedPayment.term
      } : null,
      loading,
      error,
      selectedTerm,
      selectedKmAllowance,
      paymentType
    });
  }

  return {
    selectedPayment,
    availableOptions,
    selectedTerm,
    selectedKmAllowance,
    paymentType,
    cycleTerm,
    cycleKmAllowance,
    changePaymentType,
    financeRate: getFinanceRate(),
    leaseRate: getLeaseRate(),
    loading,
    error
  };
};
